import {
  GAME_STATES,
  DIFFICULTIES,
} from '../core/Constants';
import logoImg from '../assets/images/arkanoid_logo.png';

/**
 * Detect if the device is mobile/tablet based on user-agent and touch capability
 */
function isMobileDevice() {
  const ua = navigator.userAgent || navigator.vendor || '';
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua) ||
    (navigator.maxTouchPoints > 1 && /Macintosh/i.test(ua)); // iPad Safari
}

/**
 * Commodore 64 Themed Arcade Overlay UI with Dedicated Settings Page
 */
export class OverlayUI {
  constructor(container, engine) {
    this.container = container;
    this.engine = engine;
    this.isMobile = isMobileDevice();
    this.cheatMenuVisible = false;

    this.createElements();
  }

  createElements() {
    // Hide fullscreen button on mobile devices
    const fullscreenDisplay = this.isMobile ? 'none' : 'block';

    this.container.innerHTML = `
      <!-- Top HUD with Solid Black Background -->
      <div id="c64-hud" class="c64-hud">
        <div class="hud-item"><span class="hud-label">1UP</span><span id="hud-score" class="hud-val">00000</span></div>
        <div class="hud-item"><span class="hud-label">HIGH</span><span id="hud-high" class="hud-val">50000</span></div>
        <div class="hud-item"><span class="hud-label">ROUND</span><span id="hud-round" class="hud-val">01</span></div>
        <div class="hud-item"><span class="hud-label">LIVES</span><span id="hud-lives" class="hud-val">♦♦♦</span></div>
      </div>

      <div id="modal-overlay" class="modal-overlay">
        <!-- Main Menu Screen -->
        <div id="menu-screen" class="screen-panel active">
          <div class="logo-wrapper">
            <img src="${logoImg}" alt="Arkanoid C64 Logo" class="c64-logo" />
          </div>
          <div class="c64-banner">v1.0.0</div>
          
          <div class="menu-actions">
            <button id="btn-start" class="c64-btn start-btn pulse">START</button>
            <button id="btn-open-settings-menu" class="c64-btn secondary-btn">SETTINGS</button>
          </div>

          <div class="controls-guide">
            <p>CLICK CANVAS TO LOCK MOUSE | MOUSE/ARROW KEYS TO MOVE</p>
            <p>SPACE / CLICK: LAUNCH BALL & FIRE LASERS | P: PAUSE</p>
          </div>
        </div>

        <!-- Hidden Cheat Level Select Screen -->
        <div id="cheat-screen" class="screen-panel hidden">
          <h2 class="c64-heading cyan">** WARP ZONE **</h2>
          <p class="c64-subtext">SELECT DESTINATION SECTOR</p>
          <div id="cheat-level-grid" class="cheat-level-grid"></div>
          <div class="menu-actions" style="margin-top: 14px;">
            <button id="btn-cheat-back" class="c64-btn secondary-btn">BACK</button>
          </div>
        </div>

        <!-- Dedicated Settings Screen -->
        <div id="settings-screen" class="screen-panel hidden">
          <h2 class="c64-heading cyan">** SYSTEM SETTINGS **</h2>
          
          <div class="settings-group">
            <div class="settings-title">DIFFICULTY PRESET:</div>
            <div class="settings-buttons">
              <button class="c64-btn diff-btn" data-diff="EASY">CADET (EASY)</button>
              <button class="c64-btn diff-btn active" data-diff="NORMAL">PILOT (NORMAL)</button>
              <button class="c64-btn diff-btn" data-diff="HARD">C64 MASTER (HARD)</button>
            </div>
          </div>

          <div class="settings-group">
            <div class="settings-title">HARDWARE & EFFECTS:</div>
            <div class="settings-toggles">
              <button id="btn-setting-crt" class="c64-btn toggle-btn active">CRT SHADER: ON</button>
              <button id="btn-setting-audio" class="c64-btn toggle-btn active">AUDIO: ON</button>
            </div>
          </div>

          <div class="settings-group">
            <div class="settings-title">MOUSE SENSITIVITY:</div>
            <div class="settings-buttons">
              <button class="c64-btn sens-btn" data-sens="0.8">LOW (0.8x)</button>
              <button class="c64-btn sens-btn active" data-sens="1.2">NORMAL (1.2x)</button>
              <button class="c64-btn sens-btn" data-sens="1.8">FAST (1.8x)</button>
            </div>
          </div>

          <div class="menu-actions" style="margin-top: 18px;">
            <button id="btn-settings-back" class="c64-btn start-btn">BACK</button>
          </div>
        </div>

        <!-- Pause Screen -->
        <div id="pause-screen" class="screen-panel hidden">
          <h2 class="c64-heading">** PAUSED **</h2>
          <p class="c64-subtext">COMMODORE 64 SYSTEM STANDBY</p>
          <div class="menu-actions">
            <button id="btn-resume" class="c64-btn start-btn">RESUME GAME</button>
            <button id="btn-open-settings-pause" class="c64-btn secondary-btn">SETTINGS</button>
            <button id="btn-quit-menu" class="c64-btn">QUIT TO MENU</button>
          </div>
        </div>

        <!-- Level Clear Screen -->
        <div id="level-clear-screen" class="screen-panel hidden">
          <h2 class="c64-heading pulse">ROUND CLEARED!</h2>
          <p id="level-clear-bonus" class="c64-subtext">+1000 WARP BONUS</p>
          <p class="c64-info">WARPING TO NEXT SECTOR...</p>
        </div>

        <!-- Game Over Screen -->
        <div id="game-over-screen" class="screen-panel hidden">
          <h2 class="c64-heading red">GAME OVER</h2>
          <p class="c64-subtext">ALL VAUS CRAFTS LOST</p>
          <div class="score-summary">
            <div>FINAL SCORE: <span id="final-score" class="highlight">0</span></div>
            <div>RECORD HIGH: <span id="record-high" class="highlight">50000</span></div>
          </div>
          <button id="btn-retry" class="c64-btn start-btn">PLAY AGAIN</button>
        </div>

        <!-- Victory Screen -->
        <div id="victory-screen" class="screen-panel hidden">
          <div class="boss-art-wrapper">
            <img src="${logoImg}" alt="Game Defeated" class="doh-art" />
          </div>
          <h2 class="c64-heading cyan pulse">GAME DEFEATED!</h2>
          <p class="c64-subtext">Take a screenshot of this screen and share your victory</p>
          <div class="score-summary">
            <div>VICTORY SCORE: <span id="victory-score" class="highlight">0</span></div>
          </div>
          <button id="btn-victory-restart" class="c64-btn start-btn">REPLAY MISSION</button>
        </div>
      </div>

      <!-- Bottom Quick Bar: Fullscreen button (hidden on mobile) -->
      <div class="quick-bar" style="display: ${fullscreenDisplay}">
        <button id="btn-fullscreen" class="c64-pill-btn" title="Toggle Fullscreen">FULLSCREEN</button>
      </div>
    `;

    this.populateCheatLevelGrid();
    this.bindEvents();
    this.setupFullscreenWatcher();
    this.setupCheatShortcut();

    // Request landscape orientation on mobile
    if (this.isMobile) {
      this.requestLandscapeOrientation();
    }
  }

  bindEvents() {
    // Start Button
    this.container.querySelector('#btn-start').addEventListener('click', () => {
      this.engine.startGame();
    });

    // Settings Navigation
    this.container.querySelector('#btn-open-settings-menu').addEventListener('click', () => {
      this.engine.openSettings();
      this.engine.audio.playMenuSelect();
    });

    this.container.querySelector('#btn-open-settings-pause').addEventListener('click', () => {
      this.engine.openSettings();
      this.engine.audio.playMenuSelect();
    });

    this.container.querySelector('#btn-settings-back').addEventListener('click', () => {
      this.engine.closeSettings();
      this.engine.audio.playMenuSelect();
    });

    // Pause / Resume / Quit
    this.container.querySelector('#btn-resume').addEventListener('click', () => {
      this.engine.togglePause();
    });

    this.container.querySelector('#btn-quit-menu').addEventListener('click', () => {
      this.engine.showMenu();
    });

    this.container.querySelector('#btn-retry').addEventListener('click', () => {
      this.engine.startGame();
    });

    this.container.querySelector('#btn-victory-restart').addEventListener('click', () => {
      this.engine.showMenu();
    });

    // Cheat Menu Back Button
    const cheatBackBtn = this.container.querySelector('#btn-cheat-back');
    if (cheatBackBtn) {
      cheatBackBtn.addEventListener('click', () => {
        this.hideCheatMenu();
        this.engine.audio.playMenuSelect();
      });
    }

    // Difficulty Settings Buttons
    const diffButtons = this.container.querySelectorAll('.diff-btn');
    diffButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        diffButtons.forEach((b) => b.classList.remove('active'));
        btn.classList.add('active');
        const diffKey = btn.getAttribute('data-diff');
        this.engine.setDifficulty(DIFFICULTIES[diffKey]);
        this.engine.audio.playMenuSelect();
      });
    });

    // Sensitivity Settings Buttons
    const sensButtons = this.container.querySelectorAll('.sens-btn');
    sensButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        sensButtons.forEach((b) => b.classList.remove('active'));
        btn.classList.add('active');
        const sensVal = parseFloat(btn.getAttribute('data-sens'));
        this.engine.input.mouseSensitivity = sensVal;
        this.engine.audio.playMenuSelect();
      });
    });

    // CRT Toggle Button inside Settings
    const crtBtn = this.container.querySelector('#btn-setting-crt');
    crtBtn.addEventListener('click', () => {
      const active = this.engine.renderer.toggleCRT();
      crtBtn.textContent = `CRT SHADER: ${active ? 'ON' : 'OFF'}`;
      crtBtn.classList.toggle('active', active);
      this.engine.audio.playMenuSelect();
    });

    // Audio Toggle Button inside Settings
    const audioBtn = this.container.querySelector('#btn-setting-audio');
    audioBtn.addEventListener('click', () => {
      const muted = this.engine.audio.toggleMute();
      audioBtn.textContent = `AUDIO: ${muted ? 'OFF' : 'ON'}`;
      audioBtn.classList.toggle('active', !muted);
      audioBtn.classList.toggle('muted', muted);
    });

    // Fullscreen Button
    const fsBtn = this.container.querySelector('#btn-fullscreen');
    fsBtn.addEventListener('click', () => {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen().catch(() => {});
      } else {
        document.exitFullscreen().catch(() => {});
      }
    });
  }

  setupFullscreenWatcher() {
    const fsBtn = this.container.querySelector('#btn-fullscreen');
    if (!fsBtn) return;

    const updateFsVisibility = () => {
      if (this.isMobile) {
        fsBtn.parentElement.style.display = 'none';
        return;
      }
      if (document.fullscreenElement) {
        fsBtn.style.display = 'none';
      } else {
        fsBtn.style.display = 'block';
        fsBtn.textContent = 'FULLSCREEN';
      }
    };

    document.addEventListener('fullscreenchange', updateFsVisibility);
    document.addEventListener('webkitfullscreenchange', updateFsVisibility);
    document.addEventListener('mozfullscreenchange', updateFsVisibility);
  }

  /**
   * Request landscape screen orientation on mobile devices
   */
  requestLandscapeOrientation() {
    try {
      if (screen.orientation && screen.orientation.lock) {
        screen.orientation.lock('landscape').catch(() => {
          // Silently fail — not all browsers support orientation lock
        });
      }
    } catch (e) {
      // Orientation lock not supported
    }
  }

  /**
   * Populate the cheat level-select grid with buttons for levels 1-10
   */
  populateCheatLevelGrid() {
    const grid = this.container.querySelector('#cheat-level-grid');
    if (!grid) return;

    const totalLevels = this.engine.levelManager.getTotalLevels();
    for (let i = 1; i <= totalLevels; i++) {
      const btn = document.createElement('button');
      btn.className = 'c64-btn cheat-level-btn';
      btn.textContent = `ROUND ${i.toString().padStart(2, '0')}`;
      btn.setAttribute('data-level', i.toString());
      btn.addEventListener('click', () => {
        this.engine.audio.playMenuSelect();
        this.engine.startGameAtLevel(i - 1);
        this.hideCheatMenu();
      });
      grid.appendChild(btn);
    }
  }

  /**
   * Setup the hidden cheat keyboard shortcut.
   * Press Shift+Ctrl+W (Warp) on the menu screen to open the level select.
   */
  setupCheatShortcut() {
    window.addEventListener('keydown', (e) => {
      // Shift + Ctrl + W triggers the warp zone cheat menu
      if (e.ctrlKey && (e.code === 'KeyD' || e.key === 'D' || e.key === 'd')) {
        e.preventDefault();
        if (this.engine.state === GAME_STATES.MENU || this.engine.state === GAME_STATES.PAUSED) {
          if (this.cheatMenuVisible) {
            this.hideCheatMenu();
          } else {
            this.showCheatMenu();
          }
        }
      }
    });
  }

  showCheatMenu() {
    this.cheatMenuVisible = true;
    const screens = [
      'menu-screen', 'settings-screen', 'pause-screen',
      'level-clear-screen', 'game-over-screen', 'victory-screen',
    ];
    screens.forEach((id) => {
      const el = document.getElementById(id);
      if (el) el.classList.add('hidden');
    });

    const overlay = document.getElementById('modal-overlay');
    overlay.classList.remove('hidden');
    document.getElementById('cheat-screen').classList.remove('hidden');

    this.engine.audio.playPowerupCollect && this.engine.audio.playPowerupCollect();
  }

  hideCheatMenu() {
    this.cheatMenuVisible = false;
    document.getElementById('cheat-screen').classList.add('hidden');
    this.showState(this.engine.state);
  }

  updateSettingsDisplay() {
    const crtBtn = this.container.querySelector('#btn-setting-crt');
    if (crtBtn) {
      const active = this.engine.renderer.crtEnabled === 1;
      crtBtn.textContent = `CRT SHADER: ${active ? 'ON' : 'OFF'}`;
      crtBtn.classList.toggle('active', active);
    }

    const audioBtn = this.container.querySelector('#btn-setting-audio');
    if (audioBtn) {
      const active = !this.engine.audio.muted;
      audioBtn.textContent = `AUDIO: ${active ? 'ON' : 'OFF'}`;
      audioBtn.classList.toggle('active', active);
      audioBtn.classList.toggle('muted', !active);
    }
  }

  updateHUD(score, highScore, level, lives) {
    const scoreEl = document.getElementById('hud-score');
    if (scoreEl) scoreEl.textContent = score.toString().padStart(5, '0');

    const highEl = document.getElementById('hud-high');
    if (highEl) highEl.textContent = highScore.toString().padStart(5, '0');

    const roundEl = document.getElementById('hud-round');
    if (roundEl) roundEl.textContent = level.toString().padStart(2, '0');

    const livesEl = document.getElementById('hud-lives');
    if (livesEl) {
      livesEl.textContent = '♦'.repeat(Math.max(0, lives));
    }
  }

  showState(state, data = {}) {
    const screens = [
      'menu-screen',
      'settings-screen',
      'cheat-screen',
      'pause-screen',
      'level-clear-screen',
      'game-over-screen',
      'victory-screen',
    ];
    screens.forEach((id) => {
      const el = document.getElementById(id);
      if (el) el.classList.add('hidden');
    });

    const overlay = document.getElementById('modal-overlay');

    if (state === GAME_STATES.PLAYING) {
      overlay.classList.add('hidden');
    } else {
      overlay.classList.remove('hidden');

      if (state === GAME_STATES.MENU) {
        document.getElementById('menu-screen').classList.remove('hidden');
      } else if (state === GAME_STATES.SETTINGS) {
        document.getElementById('settings-screen').classList.remove('hidden');
        this.updateSettingsDisplay();
      } else if (state === GAME_STATES.PAUSED) {
        document.getElementById('pause-screen').classList.remove('hidden');
      } else if (state === GAME_STATES.LEVEL_CLEAR) {
        document.getElementById('level-clear-screen').classList.remove('hidden');
        if (data.bonus) {
          document.getElementById('level-clear-bonus').textContent = `+${data.bonus} CLEAR BONUS`;
        }
      } else if (state === GAME_STATES.GAME_OVER) {
        document.getElementById('game-over-screen').classList.remove('hidden');
        document.getElementById('final-score').textContent = data.score || 0;
        document.getElementById('record-high').textContent = data.highScore || 50000;
      } else if (state === GAME_STATES.VICTORY) {
        document.getElementById('victory-screen').classList.remove('hidden');
        document.getElementById('victory-score').textContent = data.score || 0;
      }
    }
  }
}
